import { useQuery, useMutation } from "@tanstack/react-query";
import type {
  GetSwimmersRequest,
  Swimmer,
  SwimmerBatchCreate,
  SwimmerBatchResponse,
  GetSwimmersCountRequest,
  GetSwimmersCountResponse,
  GetSwimmerReportPreviewRequest,
  SwimmerReportDataItem,
  GetBuildingsWithSwimmersRequest,
  GetSwimmersReportDataRequest,
  UpdateSwimmerRequest,
} from "../../../api/client/types/swimmer.types";
import type {
  PaginatedResponse,
  ApiError,
  BuildingOptionsResponse,
  ReportDataResponse,
} from "../../../api/client/types/common.types";
import { swimmerEndpoints } from "../../../api/endpoints/swimmer";
import { DEFAULT_PAGE_SIZE } from "../../../shared/constants/pagination";

/**
 * Builds the default list params (page 1, no filter) for the current user.
 * Use when updating list cache after create/edit so the query key matches
 * what useSwimmersResource uses for the first page.
 */
export function getDefaultSwimmersListParams(params: {
  building_id?: string;
  lifeguard_id?: string;
}): GetSwimmersRequest {
  return {
    page: 1,
    page_size: DEFAULT_PAGE_SIZE,
    swimmer_name: undefined,
    building_id: params.building_id ?? undefined,
    lifeguard_id: params.lifeguard_id ?? undefined,
  };
}

/**
 * React Query Keys
 * Organized keys help with cache invalidation and devtools debugging.
 */
export const swimmerQueryKeys = {
  root: ["swimmers"] as const,
  list: (params?: GetSwimmersRequest) =>
    [...swimmerQueryKeys.root, "list", params] as const,
  reportPreview: (params?: GetSwimmerReportPreviewRequest) =>
    [...swimmerQueryKeys.root, "reportPreview", params] as const,
  detail: (id: string) => [...swimmerQueryKeys.root, "detail", id] as const,
  create: () => [...swimmerQueryKeys.root, "create"] as const,
  createBatch: () => [...swimmerQueryKeys.root, "createBatch"] as const,
  update: () => [...swimmerQueryKeys.root, "update"] as const,
  delete: () => [...swimmerQueryKeys.root, "delete"] as const,
  count: (params?: GetSwimmersCountRequest) => [...swimmerQueryKeys.root, "count", params] as const,
  buildingsWithSwimmers: () => [...swimmerQueryKeys.root, "buildingsWithSwimmers"] as const,
  reportData: () => [...swimmerQueryKeys.root, "reportData"] as const,
};

/**
 * Hook: useGetSwimmers
 * Fetches a paginated list of swimmers with optional filters.
 */
export const useGetSwimmers = (params?: GetSwimmersRequest) => {
  return useQuery<PaginatedResponse<Swimmer>, ApiError>({
    queryKey: swimmerQueryKeys.list(params),
    queryFn: () => swimmerEndpoints.getSwimmers(params),
  });
};

/**
 * Hook: useGetSwimmersReportPreview
 * Fetches a paginated preview of swimmer reports with optional filters.
 */
export const useGetSwimmersReportPreview = (params?: GetSwimmerReportPreviewRequest) => {
  return useQuery<PaginatedResponse<SwimmerReportDataItem>, ApiError>({
    queryKey: swimmerQueryKeys.reportPreview(params),
    queryFn: () => swimmerEndpoints.getSwimmersReportPreview(params),
  });
};

/**
 * Hook: useCreateSwimmersBatch
 * Creates multiple swimmers in a batch.
 */
export const useCreateSwimmersBatch = () => {
  return useMutation<SwimmerBatchResponse, ApiError, SwimmerBatchCreate>({
    mutationKey: swimmerQueryKeys.createBatch(),
    mutationFn: (data) => swimmerEndpoints.createSwimmersBatch(data),
  });
};

/**
 * Hook: useGetSwimmerById
 * Fetches a swimmer by ID.
 */
export const useGetSwimmerById = (
  swimmer_id: string,
  options?: { enabled?: boolean }
) => {
  return useQuery<Swimmer, ApiError>({
    queryKey: swimmerQueryKeys.detail(swimmer_id),
    queryFn: () => swimmerEndpoints.getSwimmerById(swimmer_id),
    // Allow callers to override enabled; default to "has id"
    enabled: options?.enabled ?? !!swimmer_id,
  });
};

/**
 * Hook: useUpdateSwimmer
 * Updates an existing swimmer (admin-only limited fields).
 */
export const useUpdateSwimmer = () => {
  return useMutation<
    Swimmer,
    ApiError,
    { swimmer_id: string; data: UpdateSwimmerRequest }
  >({
    mutationKey: swimmerQueryKeys.update(),
    mutationFn: ({ swimmer_id, data }) =>
      swimmerEndpoints.updateSwimmer(swimmer_id, data),
  });
};

/**
 * Hook: useDeleteSwimmer
 * Deletes a swimmer by ID (admin-only).
 */
export const useDeleteSwimmer = () => {
  return useMutation<void, ApiError, { swimmer_id: string }>({
    mutationKey: swimmerQueryKeys.delete(),
    mutationFn: ({ swimmer_id }) => swimmerEndpoints.deleteSwimmer(swimmer_id),
  });
};

/**
 * Hook: useGetSwimmersCount
 * Fetches the count of swimmers with optional filters.
 */
export const useGetSwimmersCount = (params?: GetSwimmersCountRequest) => {
  return useQuery<GetSwimmersCountResponse, ApiError>({
    queryKey: swimmerQueryKeys.count(params),
    queryFn: () => swimmerEndpoints.getSwimmersCount(params),
  });
};

/**
 * Hook: useGetBuildingsWithSwimmers
 * Fetches distinct buildings that have swimmers registered in the given date range.
 * 
 * This is a mutation hook (not cached) to ensure fresh data is fetched on each request,
 * which is important for date-dependent queries.
 * 
 * @returns Mutation hook that accepts GetBuildingsWithSwimmersRequest (start_date, end_date)
 * 
 * @example
 * ```tsx
 * const buildingsMutation = useGetBuildingsWithSwimmers();
 * 
 * // Fetch buildings
 * buildingsMutation.mutate({
 *   start_date: "01/01/2024",
 *   end_date: "31/01/2024"
 * }, {
 *   onSuccess: (data) => {
 *     console.log(data.buildings); // BuildingOption[]
 *   }
 * });
 * ```
 */
export const useGetBuildingsWithSwimmers = () => {
  return useMutation<
    BuildingOptionsResponse,
    ApiError,
    GetBuildingsWithSwimmersRequest
  >({
    mutationKey: swimmerQueryKeys.buildingsWithSwimmers(),
    mutationFn: (params) => swimmerEndpoints.getBuildingsWithSwimmers(params),
  });
};

/**
 * Hook: useGetSwimmersReportData
 * Fetches all swimmer report data for a date range without pagination.
 *
 * Uses mutation to prevent caching — always fetches fresh data on demand,
 * which is important for on-the-fly report/PDF generation.
 *
 * @example
 * ```tsx
 * const reportData = useGetSwimmersReportData();
 *
 * reportData.mutate(
 *   { start_date: "01/01/2024", end_date: "31/01/2024" },
 *   { onSuccess: (data) => exportTableToPdf({ rows: data.items, ... }) }
 * );
 * ```
 */
export const useGetSwimmersReportData = () => {
  return useMutation<
    ReportDataResponse<SwimmerReportDataItem>,
    ApiError,
    GetSwimmersReportDataRequest
  >({
    mutationKey: swimmerQueryKeys.reportData(),
    mutationFn: (params) => swimmerEndpoints.getSwimmersReportData(params),
  });
};